import * as React from "react";
import { SingleNavMenuLink, toLinkAction, toLinkButton, toLinkDivider, toLinkPage, toLinkSpace } from "../identity/useNavMenu";
import { renderEventWarningIcon } from "../identity/navMenuIcons";
import { formSet, useCpFormAnyValueId, useCpFormModifiedAnyId } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { useEventlogs } from "../../reactHooks/useEventlog";
import { linksOf } from "../../services/cpln";
import { formActions } from "../../store/formSlice";
import { Events } from "../../components/detail/events";
import { AccessReportPage } from "../../components/detail/accessReport";
import { AuditTrail } from "../../components/detail/auditTrail";
import { Secret } from "../../schema/types/secret/entity";
import { useLazyRevealSecretQuery } from "../../store/api/secretApi";
import { notification } from "antd";
import { SecretDetailHeader } from "./header";
import { Summary } from "./summary";
import { Data } from "./data/data";
import { secretToInput } from "./utils";
import { CpRootError } from "../gvc/testNewForm/CpRootError";
import { ViewModal } from "../../components/modals/viewModal";
import { confirmSecretReveal } from "../../utils/confirmSecretReveal";

interface Props {
  secret: Secret;
  refetch?: () => void;
}
export const SecretDetailContent: React.FC<Props> = ({ secret, refetch }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/secret/:secret/*`);
  const { pathname } = useLocation();

  const [revealSecret, { isLoading, isFetching }] = useLazyRevealSecretQuery();
  const [isViewModalOpen, setIsViewModalOpen] = React.useState(false);
  const [revealOnView, setRevealOnView] = React.useState(false);

  // eventlog
  const eventlogHook = useEventlogs(linksOf(secret).self!, refetch);
  React.useEffect(() => {
    eventlogHook.setItemLastModified(secret.lastModified);
  }, [secret.lastModified]);
  React.useEffect(() => {
    if (pathname.includes("/events")) {
      return;
    }

    const id = setInterval(() => eventlogHook.fetchEventlogs(true), 30 * 1000);

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [pathname]);

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);

  async function handleRevealSecret() {
    const shouldReveal = await confirmSecretReveal({ okText: "Reveal", cancelText: "Cancel" });
    if (!shouldReveal) return;

    try {
      const revealedSecret = await revealSecret(linksOf(secret).self!).unwrap();
      dispatch(formActions.setInitialValues({ id: formId, values: secretToInput(revealedSecret as Secret) }));
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to reveal secret", description: errorMessage });
    }
  }

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Summary", "/summary", { names: ["description"], showDirty: true }),
      toLinkAction("Reveal & View", {
        onClick: async () => {
          const shouldReveal = await confirmSecretReveal({ okText: "Reveal", cancelText: "Cancel" });
          if (!shouldReveal) return;
          setRevealOnView(true);
          setIsViewModalOpen(true);
        },
      }),
      toLinkPage("Data", "/data", {
        names: [
          `aws`,
          `azureConnector`,
          `azureSdk`,
          `dictionary`,
          `docker`,
          `ecr`,
          `gcp`,
          `keypair`,
          `natsAccount`,
          `opaque`,
          `tls`,
          `userpass`,
        ],
        showDirty: true,
      }),
      toLinkPage("Tags", "/tags", { names: ["tags"], showDirty: true }),
      toLinkDivider(),
      toLinkPage("Events", "/events", { renderIcon: renderEventWarningIcon(eventlogHook.hasWarning) }),
      toLinkPage("Audit Trail", "/audit-trail"),
      toLinkPage("Access Report", "/access-report"),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [anyModified, submitting, eventlogHook.hasWarning]);

  return (
    <BasePathContext.Provider value={basePath}>
      <SecretDetailHeader secret={secret} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`summary/*`} element={<Summary secret={secret} />} />
              <Route
                path={`data/*`}
                element={<Data revealSecret={handleRevealSecret} isLoading={isLoading} isFetching={isFetching} />}
              />
              <Route path={`tags/*`} element={<CpTagsNew />} />
              <Route
                path={`events`}
                element={
                  <Events
                    kind={"secret"}
                    eventlogs={eventlogHook.eventlogs}
                    isLoading={eventlogHook.isLoading}
                    fetchEventlogs={eventlogHook.fetchEventlogs}
                    isFetching={eventlogHook.isFetching}
                    nextLink={eventlogHook.nextLink}
                  />
                }
              />
              <Route
                path={`audit-trail`}
                element={
                  <AuditTrail kind={"secret"} itemName={secret.name} resourceId={secret.id} version={secret.version} selfLink={linksOf(secret).self!} refetch={refetch} />
                }
              />
              <Route path={`access-report`} element={<AccessReportPage itemLink={linksOf(secret).self!} />} />
              <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
            </Routes>
          </div>
        }
      />
      {isViewModalOpen && (
        <ViewModal
          title={`Secret: ${secret.name}`}
          kind="secret"
          link={linksOf(secret).self!}
          filename={secret.name}
          visible={isViewModalOpen}
          revealSecret={revealOnView}
          onClose={() => {
            setIsViewModalOpen(false);
            setRevealOnView(false);
          }}
        />
      )}
    </BasePathContext.Provider>
  );
};
